import mapboxgl from 'mapbox-gl'
import type { FeatureCollection, Polygon, MultiPolygon } from 'geojson'
import isFunction from 'lodash/isFunction'

/**
 * Draws all polygons from a GeoJSON file (FeatureCollection of Polygon/MultiPolygon) on the map.
 * @param map Mapbox GL map instance
 * @param geojsonUrl URL or relative path to the GeoJSON file
 * @param sourceId Optional: ID for the map source (default: 'estate-boundaries-geojson')
 * @param fillColor Optional: fill color (default: '#ff8800')
 */
export async function drawEstateBoundariesFromGeoJSON(
  map: mapboxgl.Map,
  currentSub: string,
  sourceId: string = 'estate-boundaries-geojson',
  fillColor: string = '#ff8800'
) {
  let geojsonUrl: string
  if (currentSub === '144437b7-31e2-4908-8c0f-1f1671b692a9') {
    geojsonUrl = '/data/estate_boundary/ioi_2025_10_16.geojson'
  } else if (currentSub === '295fe91c-0d27-4c2c-bf19-b8dfa185f992') {
    geojsonUrl = '/data/estate_boundary/palm_oil_demo.geojson'
  } else {
    return
  }

  // Fetch and parse GeoJSON
  const response = await fetch(geojsonUrl)
  const geojson: FeatureCollection<Polygon | MultiPolygon> = await response.json()

  // Add or update source
  const src = map.getSource(sourceId)
  if (src != null) {
    (src as mapboxgl.GeoJSONSource).setData(geojson)
  } else {
    map.addSource(sourceId, {
      type: 'geojson',
      data: geojson
    })
    map.addLayer({
      id: `${sourceId}-border`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': fillColor,
        'line-width': 2
      }
    })
  }

  // Fit map to all polygons' bounds
  const features = geojson.features
  let bounds: mapboxgl.LngLatBounds | null = null
  features.forEach((feature) => {
    const geom = feature.geometry
    let coords: number[][][] = []
    if (geom.type === 'Polygon') {
      coords = geom.coordinates as number[][][]
    } else if (geom.type === 'MultiPolygon') {
      coords = ([] as number[][][]).concat(...(geom.coordinates as number[][][][]))
    }
    coords.forEach((ring) => {
      ring.forEach(([lng, lat]) => {
        if (bounds === null) {
          bounds = new mapboxgl.LngLatBounds([lng, lat], [lng, lat])
        } else {
          bounds.extend([lng, lat])
        }
      })
    })
  })
    // Use a bound function to avoid unbound method lint error
    const fitBounds = map.fitBounds.bind(map)
    if (bounds !== null && isFunction(fitBounds)) {
      // bounds is guaranteed not null here
      fitBounds(bounds as mapboxgl.LngLatBoundsLike, { padding: 40 })
    }
}
